import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import styled from '@mint-lib/styled';
import uiShell from '@mint-lib/ui-shell';

import IconButton from '../IconButton/IconButton.jsx';
import Tooltip from '../Tooltip/Tooltip.jsx';
import { isPropValid } from './utils.js';

export type ExpandableSidebarType = {
  children?: React.ReactNode;
  isShown?: boolean;
  setIsShown?: (value: boolean) => void;
  initiallyExpanded?: boolean;
  /**
   * additional styles
   */
  className?: string;
};
export type StyledComponentProps = {
  sideBarWidth?: number;
  isOpen?: boolean;
  minWidth?: number;
  duration?: number;
  isHovering?: boolean;
  isBorderActive?: boolean;
};

const ExpandableSidebar: React.FC<ExpandableSidebarType> = ({
  children,
  isShown,
  initiallyExpanded = true,
  className,
}) => {
  const sideBarRef = useRef<HTMLDivElement>(null);

  const minWidth = 16; // in pixels
  const maxWidth = 200; // in pixels
  const duration = 300; // in milliseconds

  const [isBorderActive, setBorderActive] = useState(false);

  const [title, setTitle] = useState<string>('Expand');
  const [sideBarIsOpen, setSideBarVisibility] = useState(initiallyExpanded);
  const [cursorInSidebar, setCursorInSidebar] = useState(false);
  const [sideBarWidth, setSideBarWidth] = useState<number | undefined>(
    maxWidth,
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      sideBarIsOpen ? setTitle('Collapse') : setTitle('Expand');
    }, 100);

    uiShell.publish('sideBarIsOpen', { callback: () => sideBarIsOpen });

    return () => clearTimeout(timeout);
  }, [sideBarIsOpen]);

  useLayoutEffect(() => {
    return () => {
      setSideBarVisibility(initiallyExpanded);
      setSideBarWidth(initiallyExpanded ? maxWidth : minWidth);
    };
  }, [isShown]);

  const updateSideBarWidth = (isOpen: boolean) => {
    setSideBarWidth(isOpen ? minWidth : maxWidth);
  };

  const toggleSidebar = (isOpen: boolean) => {
    updateSideBarWidth(isOpen);
    setSideBarVisibility(!isOpen);
    setCursorInSidebar(false);
    setTitle('');
  };

  const enterSideBar = () => {
    setCursorInSidebar(true);
    if (!sideBarIsOpen) {
      updateSideBarWidth(false);
    }
  };
  const enterTrigger = () => {
    if (isHovering) {
      enterSideBar();
    }
    setBorderActive(true);
  };

  const leaveSideBar = () => {
    setCursorInSidebar(false);
    if (!sideBarIsOpen) {
      updateSideBarWidth(true);
    }
  };

  const isOpen = sideBarIsOpen;
  const isHovering = cursorInSidebar;

  const props = { isOpen, minWidth, sideBarWidth, duration, isHovering };
  return isShown ? (
    <nav onMouseLeave={leaveSideBar} className={className}>
      <Placeholder {...props} />
      <Tooltip
        title={title}
        placement="right"
        leaveTouchDelay={0}
        TransitionProps={{ timeout: 0 }}
        disableFocusListener
      >
        <Trigger
          onMouseEnter={enterTrigger}
          onMouseLeave={() => setBorderActive(false)}
          {...props}
        >
          <StyledIconButton
            onClick={() => toggleSidebar(isOpen)}
            aria-label="arrow-button"
            size="small"
            icon={isOpen ? 'ChevronLeft' : 'ChevronRight'}
            isBorderActive={isBorderActive}
          />
        </Trigger>
      </Tooltip>
      <Navigation
        onMouseEnter={enterSideBar}
        data-open={isOpen}
        isBorderActive={isBorderActive}
        {...props}
      >
        <Content ref={sideBarRef} {...props}>
          {children}
        </Content>
      </Navigation>
    </nav>
  ) : null;
};

export default ExpandableSidebar;

const Placeholder = styled('div', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<StyledComponentProps>(
  ({ isOpen, sideBarWidth, minWidth, isHovering, duration }) => ({
    width: isOpen ? `${sideBarWidth}px` : `${minWidth}px`,
    transitionDuration: `${duration}ms`,
    transitionDelay: `${!isOpen && isHovering ? duration : 0}ms`,
    position: 'relative',
  }),
);
const Trigger = styled('div', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<StyledComponentProps>(({ isOpen, sideBarWidth, isHovering, duration }) => ({
  left: `calc(47.5px + ${sideBarWidth}px)`,
  top: '4rem',
  transitionDuration: `${duration}ms`,
  transitionDelay: `${!isOpen && isHovering ? duration : 0}ms`,
  position: 'fixed',
  zIndex: 4,
  transform: 'translateX(-50%)',
  opacity: 1,
}));
const Navigation = styled('nav', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<StyledComponentProps>(
  ({ theme, isOpen, sideBarWidth, isHovering, duration, isBorderActive }) => ({
    overflow: 'hidden',
    backgroundColor: theme.palette.ui.primary,
    boxSizing: 'border-box',
    height: '100%',
    borderRight: isBorderActive
      ? `1px solid ${theme.palette.ui.inverse01}`
      : `1px solid ${theme.palette.ui.tertiary}`,
    width: `${sideBarWidth}px`,
    transition: `width ${duration}ms, background-color ${duration}ms`,
    transitionDelay: `${!isOpen && isHovering ? duration : 0}ms`,
    boxShadow:
      !isOpen && isHovering ? '0px 0px 8px 0px rgb(0 0 0 / 16%)' : 'none',
  }),
);
const Content = styled('div', {
  shouldForwardProp: (prop) => isPropValid(prop),
})<StyledComponentProps>(({ isOpen, isHovering, duration }) => ({
  padding: '1rem 0.5rem 0.5rem 0.5rem',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',
  height: '100%',
  opacity: !isHovering && !isOpen ? 0 : '100%',
  transitionDuration: `${duration}ms`,
  transitionDelay: `${!isOpen && isHovering ? duration : 0}ms`,
}));
const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isBorderActive',
})<{ isBorderActive?: boolean }>(({ theme, isBorderActive }) => ({
  display: 'block',
  appearance: 'none',
  border: isBorderActive
    ? `1px solid ${theme.palette.ui.inverse01}`
    : `1px solid ${theme.palette.ui.tertiary}`,
  backgroundColor: isBorderActive
    ? `${theme.palette.ui.tertiary} !important`
    : `${theme.palette.ui.primary} !important`,
  borderRadius: '100%',
  padding: '3px',
  zIndex: 4,
  lineHeight: 1,
  '&:focus': {
    boxShadow: 'none',
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.primary,
  },
}));
